/**
 * yardDropInstrumentation — iter386c / D1 / iter386β5 diagnostic
 * instrumentation.
 *
 * Records structured entries for every Yard drop attempt so we can
 * distinguish between drops that register (item enters a Stack) and
 * drops that don't.  The buffer holds the most recent 50 entries in
 * memory and mirrors them to **localStorage** (iter386β5 — moved
 * from sessionStorage so the evidence survives tab-close and is
 * readable from any tab, not just the tab that produced it).  No
 * console output.  Consumed by the Admin Diagnostics page.
 *
 * IMPORTANT — this module MUST NOT change any drop-resolution
 * behaviour.  It only observes.
 */

const KEY = 'yard.drop.attempts.v1';
const MAX = 50;

let _buffer = null; // hydrated on first access

function _load() {
  if (_buffer) return _buffer;
  _buffer = [];
  if (typeof localStorage === 'undefined') return _buffer;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) _buffer = parsed.slice(-MAX);
    }
  } catch (_e) { /* corrupt storage — start fresh */ }
  // iter386β5 — one-time migration: if the older sessionStorage entry
  // still exists (previous build), fold it in and clear the session
  // key.  Never throws.
  try {
    if (typeof sessionStorage !== 'undefined') {
      const legacy = sessionStorage.getItem(KEY);
      if (legacy) {
        try {
          const parsed = JSON.parse(legacy);
          if (Array.isArray(parsed) && parsed.length > 0) {
            _buffer = [..._buffer, ...parsed].slice(-MAX);
          }
        } catch (_e) { /* ignore malformed legacy */ }
        sessionStorage.removeItem(KEY);
      }
    }
  } catch (_e) { /* ignore storage errors */ }
  return _buffer;
}

function _persist() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(_buffer.slice(-MAX)));
  } catch (_e) { /* quota — ignore */ }
}

/**
 * Record one drop-attempt entry.  Never throws.
 * Fields (all optional; caller passes whichever it knows at record time):
 *   - timestamp: ISO string (default: now)
 *   - draggedId, draggedType
 *   - mouseDownClient: { x, y }
 *   - mouseUpClient:   { x, y }
 *   - cursorCanvas:    { x, y }
 *   - grabOffset:      { x, y }
 *   - devicePixelRatio
 *   - zoom, panOffset: { x, y }
 *   - candidates: [ { id, item_type, bounds:{left,top,right,bottom}, passed:bool, reason:string } ]
 *   - chosen: { kind: 'stack'|'block'|'canvas', id: string|null, reason: string }
 *   - resultingContainerId: string | null
 *   - notes: freeform string
 */
export function recordDropAttempt(entry) {
  const buf = _load();
  const stamped = { timestamp: new Date().toISOString(), ...entry };
  buf.push(stamped);
  if (buf.length > MAX) buf.splice(0, buf.length - MAX);
  _persist();
  return stamped;
}

export function getDropAttempts() {
  return _load().slice();
}

export function clearDropAttempts() {
  _buffer = [];
  _persist();
}

/**
 * Serialise the current buffer to a compact, human-readable string
 * for the copy-to-clipboard control in the Admin Diagnostics panel.
 */
export function serialiseDropAttempts() {
  const buf = getDropAttempts();
  return JSON.stringify(buf, null, 2);
}
